import {
  DownloadIcon,
  NoteIcon,
  SyncIcon
} from "@primer/octicons-react";
import { Box, Text, Select } from "@primer/react";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { crmHocSinhApi } from "../../api/crmHocSinhApi";

import { ComboboxTruong } from "../../components/combobox-truong";
import HocSinhShortInfo from "../../components/hoc-sinh/HocSinhShortInfo";
import SelectBoxTrangThaiHocSinhMultiple from "../../components/selectbox-trangthai-hocsinh-multiple";
import Button from "../../components/ui/button";
import DataTable from "../../components/ui/data-table";
import TextWithEllipse from "../../components/ui/text-with-ellipse";
import { useCommonContext } from "../../contexts/common";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import { ePageBaseStatus } from "../../models/ePageBaseStatus";
import { IHocSinhSelectRequest } from "../../models/request/hoc-sinh/IHocSinhSelectRequest";
import { IHocSinhTongHopSucKhoeItemResponse } from "../../models/response/crm-hocsinh/IHocSinhTongHopSucKhoeItemResponse";
import { RootState } from "../../state/reducers";
import SucKhoeModal from "../hoc-sinh-profile/suc-khoe/SucKhoeModal";

interface HocSinh extends IHocSinhTongHopSucKhoeItemResponse { }

// Component combobox filter sức khỏe
const SucKhoeFilterCombobox = ({
  value,
  onValueChanged
}: {
  value: string;
  onValueChanged: (value: string) => void;
}) => {
  const { translate } = useCommonContext();

  const options = [
    { value: "all", label: translate("Tất cả") },
    { value: "co_van_de", label: translate("Có vấn đề sức khỏe") },
    { value: "khong_co_van_de", label: translate("Không có vấn đề sức khỏe") },
  ];

  return (
    <Select
      value={value}
      onChange={(e) => onValueChanged(e.target.value)}
      sx={{ minWidth: 100 }}
    >
      {options.map((option) => (
        <Select.Option key={option.value} value={option.value}>
          {option.label}
        </Select.Option>
      ))}
    </Select>
  );
};

const HocSinhSucKhoe = ({
  onValueChanged,
}: {
  onValueChanged?: (values: { dm_trangthaihocsinh_ids: string }) => void;
}) => {
  const { status } = useSelector((state: RootState) => state.crmHocSinh);
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
  const { translate } = useCommonContext();

  const [selectedTruongId, setSelectedTruongId] = useState<number>(0);
  const [dm_trangthaihocsinh_ids, setDmTrangThaiIds] = useState<number[]>([]);
  const [isShowSucKhoeModal, setIsShowSucKhoeModal] = useState(false);
  const [selectedHocSinhId, setSelectedHocSinhId] = useState<number>(0);
  const [allHocSinhs, setAllHocSinhs] = useState<HocSinh[]>([]);
  const [filteredHocSinhs, setFilteredHocSinhs] = useState<HocSinh[]>([]);
  const [sucKhoeFilter, setSucKhoeFilter] = useState<string>("all"); // "all", "co_van_de", "khong_co_van_de"
  const [allSucKhoeColumns, setAllSucKhoeColumns] = useState<any[]>([]);

  const handleRowClick = (data: HocSinh) => {
    setSelectedHocSinhId(data.hocsinh_id);
    setIsShowSucKhoeModal(true);
  };

  useEffect(() => {
    const loadAllHocSinh = async () => {
      // Chỉ load khi đã chọn trường
      if (!selectedTruongId || selectedTruongId === 0) {
        setAllHocSinhs([]);
        setFilteredHocSinhs([]);
        return;
      }

      const payload: IHocSinhSelectRequest = {
        nam_hoc: nam_hoc,
        searchText: "",
        dm_trangthaihocsinh_ids: [],
        dm_truong_id: selectedTruongId,
        dm_coso_id: dm_coso_id || 1,
        dm_khoi_id: 0,
        dm_he_id: 0,
        dm_lop_id: 0,
      };
      const res = await crmHocSinhApi.SelectTongHopSucKhoe(payload);
      if (res.is_success) {
        console.log('SelectTongHopSucKhoe response:', res.data);
        // Debug: Kiểm tra dữ liệu sức khỏe của học sinh đầu tiên
        if (res.data.length > 0) {
          console.log('First student health data:', res.data[0].danhSachSucKhoe);
        }
        setAllHocSinhs(res.data);
        setFilteredHocSinhs(res.data);
      }
    };

    loadAllHocSinh();
  }, [nam_hoc, selectedTruongId, dm_coso_id]);



  // Tạo dynamic columns cho sức khỏe với grouped headers
  const generateSucKhoeColumns = useCallback(() => {
    if (!allHocSinhs || allHocSinhs.length === 0) {
      setAllSucKhoeColumns([]);
      return;
    }

    // Collect tất cả các vấn đề sức khỏe unique
    const allSucKhoeMap = new Map();

    allHocSinhs.forEach(hs => {
      if (hs.danhSachSucKhoe) {
        hs.danhSachSucKhoe.forEach(sk => {
          const key = `${sk.suckhoe_id}_${sk.ten_suc_khoe}`;
          if (!allSucKhoeMap.has(key)) {
            allSucKhoeMap.set(key, {
              suckhoe_id: sk.suckhoe_id,
              ten_suc_khoe: sk.ten_suc_khoe,
              loai_suc_khoe: sk.loai_suc_khoe || "Khác"
            });
          }
        });
      }
    });

    // Group theo loại sức khỏe
    const groupedByType = Array.from(allSucKhoeMap.values()).reduce((acc: any, sk) => {
      const loai = sk.loai_suc_khoe;
      if (!acc[loai]) {
        acc[loai] = [];
      }
      acc[loai].push(sk);
      return acc;
    }, {});

    // Tạo grouped columns
    const columns: any[] = [];

    Object.keys(groupedByType).forEach(loaiSucKhoe => {
      const sucKhoeList = groupedByType[loaiSucKhoe];

      // Tạo group column với sub-columns
      const groupColumn = {
        id: `group_${loaiSucKhoe.replace(/\s+/g, '_')}`,
        caption: loaiSucKhoe,
        width: sucKhoeList.length * 200, // Width của group = số sub-columns * 200px
        columns: sucKhoeList.map((sk: any) => ({
          id: `suckhoe_${sk.suckhoe_id}`,
          caption: sk.ten_suc_khoe,
          width: 200,
          minWidth: 200,
          cellRender: (data: HocSinh) => {
            const sucKhoeItem = data.danhSachSucKhoe?.find(item => item.suckhoe_id === sk.suckhoe_id);
            const chiTiet = sucKhoeItem?.chiTietSucKhoe?.[0];
            const hasData = chiTiet && chiTiet.is_tick;

            return (
              <Box sx={{ padding: 1, textAlign: "center" }}>
                <Text sx={{
                  fontSize: 1,
                  fontWeight: "600",
                  color: hasData ? "success.emphasis" : "text.secondary",
                  display: "block",
                  marginBottom: 1
                }}>
                  {hasData ? "Có" : "Không"}
                </Text>
                {hasData && chiTiet && chiTiet.ghi_chu && (
                  <Box sx={{ padding: 1, textAlign: "left" }}>
                    <TextWithEllipse
                      text={chiTiet.ghi_chu}
                      lineNumber={1}
                    />
                  </Box>
                )}
              </Box>
            );
          }
        }))
      };

      columns.push(groupColumn);
    });

    setAllSucKhoeColumns(columns);
  }, [allHocSinhs]);

  useEffect(() => {
    generateSucKhoeColumns();
  }, [generateSucKhoeColumns]);

  useEffect(() => {
    let filtered = allHocSinhs;

    // Filter theo tình trạng sức khỏe
    if (sucKhoeFilter === "co_van_de") {
      filtered = filtered.filter(hs =>
        hs.danhSachSucKhoe && hs.danhSachSucKhoe.length > 0 &&
        hs.danhSachSucKhoe.some(sk =>
          sk.chiTietSucKhoe && sk.chiTietSucKhoe.length > 0 &&
          sk.chiTietSucKhoe.some(ct => ct.is_tick === true)
        )
      );
    } else if (sucKhoeFilter === "khong_co_van_de") {
      filtered = filtered.filter(hs =>
        !hs.danhSachSucKhoe || hs.danhSachSucKhoe.length === 0 ||
        !hs.danhSachSucKhoe.some(sk =>
          sk.chiTietSucKhoe && sk.chiTietSucKhoe.length > 0 &&
          sk.chiTietSucKhoe.some(ct => ct.is_tick === true)
        )
      );
    }

    setFilteredHocSinhs(filtered);
  }, [allHocSinhs, sucKhoeFilter]);

  const handleReload = useCallback(() => {
    // Chỉ reload khi đã chọn trường
    if (!selectedTruongId || selectedTruongId === 0) {
      return;
    }

    const payload: IHocSinhSelectRequest = {
      nam_hoc: nam_hoc,
      searchText: "",
      dm_trangthaihocsinh_ids: dm_trangthaihocsinh_ids,
      dm_truong_id: selectedTruongId,
      dm_coso_id: dm_coso_id,
      dm_khoi_id: 0,
      dm_he_id: 0,
      dm_lop_id: 0,
    };

    const fetchData = async () => {
      const res = await crmHocSinhApi.SelectTongHopSucKhoe(payload);
      if (res.is_success) {
        setAllHocSinhs(res.data);
        setFilteredHocSinhs(res.data);
      }
    };

    fetchData();
  }, [nam_hoc, dm_trangthaihocsinh_ids, selectedTruongId, dm_coso_id]);

  useEffect(() => {
    if (
      status === ePageBaseStatus.is_not_initialization ||
      status === ePageBaseStatus.is_need_reload
    ) {
      handleReload();
    }
  }, [status, handleReload]);

  const handleExportExcel = () => {
    // Collect tất cả các vấn đề sức khỏe unique để tạo columns
    const allSucKhoeMap = new Map();

    filteredHocSinhs.forEach(hs => {
      if (hs.danhSachSucKhoe) {
        hs.danhSachSucKhoe.forEach(sk => {
          const key = `${sk.suckhoe_id}_${sk.ten_suc_khoe}`;
          if (!allSucKhoeMap.has(key)) {
            allSucKhoeMap.set(key, {
              suckhoe_id: sk.suckhoe_id,
              ten_suc_khoe: sk.ten_suc_khoe,
              loai_suc_khoe: sk.loai_suc_khoe || "Khác"
            });
          }
        });
      }
    });

    // Tạo dữ liệu export với dynamic columns
    const exportData: any[] = [];

    filteredHocSinhs.forEach((hocSinh) => {
      const rowData: any = {
        "Trạng thái": hocSinh.trang_thai || "Chưa xác định",
        "Mã học sinh": hocSinh.ma_hs,
        "Họ tên": hocSinh.ho_ten,
        "Ngày sinh": hocSinh.ngay_sinh ? moment(hocSinh.ngay_sinh).format("DD/MM/YYYY") : "",
        "Trường": hocSinh.ten_truong,
        "Hệ": hocSinh.ten_he,
        "Khối": hocSinh.ten_khoi,
        "Lớp": hocSinh.ten_lop,
      };

      // Thêm các cột sức khỏe dynamic
      Array.from(allSucKhoeMap.values()).forEach((sk: any) => {
        const sucKhoeItem = hocSinh.danhSachSucKhoe?.find(item => item.suckhoe_id === sk.suckhoe_id);
        const chiTiet = sucKhoeItem?.chiTietSucKhoe?.[0];
        const hasData = chiTiet && chiTiet.is_tick;

        // Tạo column name với format: "Loại sức khỏe - Tên sức khỏe"
        const columnName = `${sk.loai_suc_khoe} - ${sk.ten_suc_khoe}`;

        if (hasData) {
          rowData[columnName] = chiTiet && chiTiet.ghi_chu ?
            `Có - ${chiTiet.ghi_chu}` : "Có";
        } else {
          rowData[columnName] = "Không";
        }
      });

      exportData.push(rowData);
    });

    // Tạo worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    if (!ws["!ref"]) {
      return;
    }

    // Thiết lập độ rộng cột
    const baseColumnWidths = [
      { wch: 15 }, // Trạng thái
      { wch: 15 }, // Mã học sinh
      { wch: 25 }, // Họ tên
      { wch: 12 }, // Ngày sinh
      { wch: 30 }, // Trường
      { wch: 10 }, // Hệ
      { wch: 10 }, // Khối
      { wch: 10 }, // Lớp
    ];

    // Thêm width cho các cột sức khỏe dynamic
    const sucKhoeColumnWidths = Array.from(allSucKhoeMap.values()).map(() => ({ wch: 25 }));
    const columnWidths = [...baseColumnWidths, ...sucKhoeColumnWidths];
    ws["!cols"] = columnWidths;

    // Style cho header
    const headerStyle = {
      font: {
        bold: true,
        color: { rgb: "FFFFFF" },
      },
      fill: {
        fgColor: { rgb: "4472C4" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      border: {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      },
    };

    const range = XLSX.utils.decode_range(ws["!ref"]);
    const headerRange = {
      s: { r: 0, c: 0 },
      e: { r: 0, c: range.e.c },
    };

    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!ws[cellRef]) continue;
      ws[cellRef].s = headerStyle;
    }

    ws["!rows"] = [{ hpt: 30 }];

    // Tạo workbook và xuất file
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tổng hợp sức khỏe");

    const fileName = `Tong_hop_suc_khoe_${moment().format("DD-MM-YYYY")}.xlsx`;
    XLSX.writeFile(wb, fileName, { bookType: "xlsx", type: "binary" });
  };



  return (
    <Box
      sx={{
        p: 3,
        overflowX: "auto",
      }}
    >
      <DataTable
        height={`${window.innerHeight - 230}px`}
        data={filteredHocSinhs}
        isLoading={status === ePageBaseStatus.is_loading}
        title={translate("Tổng hợp sức khỏe")}
        subTitle={`${translate("Base.Label.TotalCount")}: ${filteredHocSinhs.length}`}
        searchEnable
        paging={{
          enable: true,
          pageSizeItems: [20, 50, 200, 500, 1000],
        }}
        actionComponent={
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "flex-end",
            }}
          >
            <ComboboxTruong
              value={selectedTruongId}
              onValueChanged={(value) => {
                setSelectedTruongId(value);
                // Reset dữ liệu khi đổi trường
                setAllHocSinhs([]);
                setFilteredHocSinhs([]);
              }}
              isShowClearButton={true}
            />

            <SelectBoxTrangThaiHocSinhMultiple
              selectedValue={dm_trangthaihocsinh_ids}
              onSelectionChanged={(ids) => {
                setDmTrangThaiIds(ids);
                if (onValueChanged) {
                  onValueChanged({
                    dm_trangthaihocsinh_ids: ids.join(","),
                  });
                }
              }}
              maxWidth={250}
              isShowClearBtn={true}
              hocSinhs={allHocSinhs}
            />

            <SucKhoeFilterCombobox
              value={sucKhoeFilter}
              onValueChanged={setSucKhoeFilter}
            />

            <Button
              text="Base.Label.Refresh"
              leadingVisual={SyncIcon}
              size="medium"
              onClick={() => {
                handleReload();
              }}
            />

            <Button
              text="Export"
              leadingVisual={DownloadIcon}
              size="medium"
              variant="default"
              onClick={handleExportExcel}
            />
          </Box>
        }

        columns={[
          {
            id: "group_thong_tin_hoc_sinh",
            caption: translate("Thông tin học sinh"),
            width: 800, 
            columns: [
              {
                id: "trang_thai",
                caption: translate("Trạng thái"),
                width: 120,
                cellRender: (data: HocSinh) => (
                  <Box sx={{ padding: 1 }}>
                    <Text sx={{
                      fontSize: 1,
                      fontWeight: "500",
                    }}>
                      <TextWithEllipse text={data.trang_thai} lineNumber={1} />
                    </Text>
                  </Box>
                ),
              },
              {
                dataField: "ma_hs",
                width: 100,
                align: "center",
                caption: translate("Mã học sinh"),
                fixed: true
              },
              {
                id: "ho_ten",
                caption: translate("Họ tên"),
                width: 250,
                cellRender: (data: HocSinh) => (
                  <HocSinhShortInfo
                    data={data}
                    onClick={() => handleRowClick(data)}
                  />
                ),
              },
              {
                id: "thong_tin_truong_lop",
                caption: translate("Trường - Lớp"),
                width: 200,
                cellRender: (data: HocSinh) => {
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "baseline",
                        // fontWeight: 600
                      }}
                    >
                      <Box sx={{ display: "flex" }}>
                        <Box sx={{ fontWeight: 600 }}>{data.ten_lop}</Box>
                      </Box>
                      <Box sx={{ color: "fg.muted" }}>
                        {data.ten_khoi}, Hệ {data.ten_he}
                      </Box>
                    </Box>
                  );
                },
              }
            ]
          },
          // Dynamic sức khỏe columns
          ...allSucKhoeColumns
        ]}
      />

      {isShowSucKhoeModal && selectedHocSinhId > 0 && (
        <SucKhoeModal
          hocSinhId={selectedHocSinhId}
          onClose={() => setIsShowSucKhoeModal(false)}
        />
      )}
    </Box>
  );
};

export default HocSinhSucKhoe;
